<script setup lang="ts">
import type { MapStyle, MapStyleKey } from '~/composables/useMapStyles'
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

const props = withDefaults(
  defineProps<{
    modelValue: MapStyleKey
    direction?: 'left' | 'bottom-right' | 'bottom-left'
    dark?: boolean
    hiddenStyles?: MapStyleKey[]
    disableProGuard?: boolean
  }>(),
  {
    direction: 'left',
    dark: false,
    hiddenStyles: () => [],
    disableProGuard: false
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: MapStyleKey): void
}>()

const { styles } = useMapStyles()
const { requirePro } = useProGate()
const { trackEvent } = useAnalyticsTrackingService()
const { t } = useI18n()
const authStore = useAuthStore()

const root = ref<HTMLElement | null>(null)

const state = reactive({
  open: false,
  openedByHover: false
})

const isUserPro = computed(() => authStore.isUserPro)

const visibleStyles = computed(() =>
  styles.filter((style) => !props.hiddenStyles.includes(style.key))
)

const showProBadge = computed(() => !props.disableProGuard && !isUserPro.value)

const popoverClasses = computed(() => {
  if (props.direction === 'left') return 'tw-right-full tw-top-0 tw-pr-2'

  // Both `bottom-*` variants drop below the trigger and differ only in which
  // edge they grow from, so the panel stays inside the viewport whichever side
  // of the map the trigger sits on.
  return props.direction === 'bottom-left'
    ? 'tw-right-0 tw-top-full tw-pt-2'
    : 'tw-left-0 tw-top-full tw-pt-2'
})

const toggleOpen = () => {
  // A click always takes over control, so the picker no longer auto-closes on
  // mouse leave, even if it was originally opened by hovering.
  if (state.open && state.openedByHover) {
    state.openedByHover = false
    return
  }

  state.open = !state.open
  state.openedByHover = false
}

const openOnHover = () => {
  if (state.open) return

  state.open = true
  state.openedByHover = true
}

const closeOnLeave = () => {
  if (!state.openedByHover) return

  state.open = false
  state.openedByHover = false
}

const selectStyle = (style: MapStyle) => {
  if (!props.disableProGuard && style.isPro && !isUserPro.value) {
    requirePro('satellite_map')
    return
  }

  trackEvent(ANALYTICS_EVENTS.settingMapTypeChange(style.key, ''))

  emit('update:modelValue', style.key)

  // When opened via hover, keep it open on select — it will close on mouse
  // leave. When opened/pinned via click, close it as before.
  if (state.openedByHover) return

  state.open = false
}

useClickOutside(root, () => {
  state.open = false
})
</script>

<template>
  <div ref="root" class="tw-relative" @mouseenter="openOnHover" @mouseleave="closeOnLeave">
    <button
      class="normalize-style tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-shadow"
      :class="dark ? 'tw-bg-gray-800' : 'tw-bg-white'"
      @click="toggleOpen"
    >
      <FontAwesomeIcon
        :icon="['far', 'map']"
        class="tw-h-4 tw-w-4"
        :class="dark ? 'tw-text-white/70' : 'tw-text-black/70'"
      />
    </button>

    <div v-if="state.open" class="tw-absolute tw-z-50" :class="popoverClasses">
      <div
        class="tw-grid tw-w-max tw-grid-cols-3 tw-gap-3 tw-rounded-2xl tw-p-3 tw-shadow-lg"
        :class="dark ? 'tw-bg-gray-800' : 'tw-bg-white'"
      >
        <button
          v-for="style in visibleStyles"
          :key="style.key"
          class="normalize-style tw-flex tw-w-16 tw-flex-col tw-items-center tw-gap-1"
          @click="selectStyle(style)"
        >
          <span
            class="tw-relative tw-h-12 tw-w-12 tw-shrink-0 tw-rounded-full tw-bg-cover tw-bg-center tw-shadow"
            :class="
              style.key === props.modelValue
                ? dark
                  ? 'tw-ring-2 tw-ring-riser-blue tw-ring-offset-2 tw-ring-offset-gray-800'
                  : 'tw-ring-2 tw-ring-riser-blue tw-ring-offset-2'
                : ''
            "
            :style="{ backgroundImage: `url(${style.previewImage})` }"
          >
            <IconsProBadge
              v-if="style.isPro && showProBadge"
              class="tw-absolute -tw-right-1 -tw-top-1 tw-h-4 tw-w-4 tw-rounded-full tw-border"
              :class="dark ? 'tw-border-gray-800' : 'tw-border-white'"
            />
          </span>

          <span
            class="tw-text-center tw-text-xs tw-leading-tight"
            :class="dark ? 'tw-text-white/70' : 'tw-text-black/70'"
          >
            {{ t(style.labelKey) }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
